import { apiClient } from './apiClient';

/** What the catalogue says about one gateway command. Mirrors app/v2/commands/<TYPE>.yaml. */
export type CommandOp = 'read' | 'write' | 'delete' | 'clear' | 'action';

export interface ScalarSpec {
  type: 'enum' | 'int' | 'text' | 'phone' | 'phone_list' | 'time_hhmm';
  enum?: Record<string, string>;
  min?: number;
  max?: number;
  default?: string | number;
  unit?: string;
  width?: number;
  max_length?: number;
  max_items?: number;
  pattern?: string;
  multiline?: boolean;
  secret?: boolean;
  optional?: boolean;
  hints?: Record<string, string>;
}

export interface CompositePart extends ScalarSpec {
  name: string;
  label: string;
}

export interface CompositeSpec {
  type: 'composite';
  parts: CompositePart[];
  joiner?: string;
  default?: string;
}

export type ValueSpec = ScalarSpec | CompositeSpec;

export interface ProgrammingCommand {
  code: string;
  cmd: string;
  legacy_cmd?: string;
  label: string;
  group: string;
  ops: CommandOp[];
  value: ValueSpec | null;
  indexed?: { name: string; min: number; max: number };
  dangerous?: boolean;
  secret?: boolean;
  linked?: string;
  note?: string;
}

export interface ProgrammingCatalogue {
  type: string;
  groups: { code: string; label: string }[];
  commands: ProgrammingCommand[];
}

export type ReadStatus = 'ok' | 'timeout' | 'offline';
export type WriteStatus = 'applied' | 'mismatch' | 'sent';

export interface ReadItem {
  code: string;
  cmd: string;
  op: 'read';
  status: ReadStatus;
  sent?: string;
  reply?: string;
  raw?: string | null;
  value?: unknown;
  latency_ms?: number | null;
}

export interface WriteItem {
  code: string;
  cmd: string;
  op: CommandOp;
  status: WriteStatus;
  sent: string;
  reply?: string | null;
  value?: unknown;
  read_back?: unknown;
  raw?: string | null;
  latency_ms?: number | null;
}

export interface PingResult {
  device_id: string;
  online: boolean;
  latency_ms: number | null;
  sent: string;
  reply: string | null;
  identification: string | null;
}

export interface RawResult {
  sent: string;
  reply: string | null;
  latency_ms: number | null;
  cmd: string | null;
  raw: string | null;
  status: 'ok' | 'timeout';
}

/** POST /v2/programming/{id}/call — the gateway dials the environment's probe number. */
export interface CallResult {
  sent: string;
  number: string;
  line: number;
  duration: number;
  status: 'sent' | 'accepted' | 'callerid' | 'error';
  event: string | null;
  /** The gateway's own line, read from its callerid event (E.164 digits). */
  line_number: string | null;
  reply: string | null;
  latency_ms: number | null;
}

export interface ProgrammingChange {
  code: string;
  op: CommandOp;
  value?: unknown;
  index?: number;
}

const base = (deviceId: string) => `/v2/programming/${encodeURIComponent(deviceId)}`;

class ProgrammingService {
  getCatalogue(deviceId: string): Promise<ProgrammingCatalogue> {
    return apiClient.get(`${base(deviceId)}/catalogue`);
  }

  ping(deviceId: string): Promise<PingResult> {
    return apiClient.post(`${base(deviceId)}/ping`);
  }

  async read(deviceId: string, codes: string[], index?: number): Promise<ReadItem[]> {
    const response = await apiClient.post(`${base(deviceId)}/read`, { codes, ...(index ? { index } : {}) });
    return Array.isArray(response?.items) ? response.items : [];
  }

  async write(deviceId: string, changes: ProgrammingChange[]): Promise<WriteItem[]> {
    const response = await apiClient.post(`${base(deviceId)}/write`, { changes });
    return Array.isArray(response?.items) ? response.items : [];
  }

  raw(deviceId: string, payload: string): Promise<RawResult> {
    return apiClient.post(`${base(deviceId)}/raw`, { payload });
  }

  /** Make the gateway dial the environment's probe number (CALL_PROBE_* on the backend). */
  call(deviceId: string): Promise<CallResult> {
    return apiClient.post(`${base(deviceId)}/call`);
  }
}

export const programmingService = new ProgrammingService();

/** The message an API error carries, for the log and the notices. */
export const errorDetail = (err: unknown): string => {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (e?.response?.status) return `HTTP ${e.response.status}`;
  return e?.message ?? 'error';
};

export const errorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;
